import { useState, useEffect } from 'react';
import { 
  PageHeader, 
  Tabs, 
  Button,
  Badge, 
  Card,
  CardContent
} from '../../components/ui';
import { JobDetailSheet } from '../../components/jobs/JobDetailSheet';
import { ResumeSwitcher } from '../../components/ResumeSwitcher';
import { useResumeContext } from '../../hooks/useResumeContext';
import { apiFetch } from '../../lib/api-client';
import { 
  Bookmark, 
  Send, 
  Archive,
  Building
} from 'lucide-react';

export default function SavedPage() {
  const { } = useResumeContext();
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  
  // Live data from API
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved jobs when component mounts
  useEffect(() => {
    loadSavedJobs();
  }, []);

  const loadSavedJobs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await apiFetch('/jobs/saved');
      
      if (!res.ok) {
        throw new Error(`Failed to load saved jobs: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.success) {
        const jobs = (data.data?.items || []).map((job: any) => ({
          ...job,
          fitPercentage: Math.round(Math.random() * 40 + 60), // Mock fit percentage
          strategy: job.strategy || 'RESUME_TAGS',
          tags: job.tags || ['React', 'TypeScript'],
          bucket: job.bucket || 'to-apply' // Default bucket
        }));
        setSavedJobs(jobs);
      } else {
        throw new Error(data.error?.message || 'Failed to load saved jobs');
      }
    } catch (e: any) {
      console.error('Failed to load saved jobs:', e);
      setError(e?.message || 'Failed to load saved jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (jobId: string) => {
    const job = savedJobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob({
        ...job,
        description: 'We are looking for a talented software engineer...',
        salaryMin: job.salaryMin || 120000,
        salaryMax: job.salaryMax || 180000,
        employment_type: job.employment_type || 'Full-time'
      });
      setSheetOpen(true);
    }
  };

  const handleBulkAction = (action: string) => {
    console.log(`Bulk ${action}:`, selectedJobs);
    setSelectedJobs([]);
  };

  const toggleSelection = (jobId: string) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const getStrategyColor = (strategy: string) => {
    switch (strategy) {
      case 'HIGH_COMP': return 'bg-info';
      case 'EASY_WIN': return 'bg-positive';
      case 'SMALL_CO': return 'bg-caution';
      case 'REMOTE': return 'bg-primary';
      default: return 'bg-neutral';
    }
  };

  const renderJobCard = (job: any) => (
    <div key={job.id} className="relative">
      {/* Left color strip for strategy */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStrategyColor(job.strategy)} rounded-l-2xl z-10`} />
      
      <Card className="h-full ml-1 border border-border hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 active:bg-muted/50 transition-all">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Header with checkbox and title */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={selectedJobs.includes(job.id)}
                onChange={() => toggleSelection(job.id)}
                className="mt-1 focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-medium text-foreground truncate">{job.title}</h3>
                  <div className="flex items-center gap-1 bg-gradient-primary text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm shrink-0">
                    <span>Fit {job.fitPercentage}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Building className="h-3 w-3" />
                  <span className="truncate">{job.company}</span>
                  <span>•</span>
                  <span className="truncate">{job.location}</span>
                </div>
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  {job.salary}
                </div>
                <div className="flex items-center gap-1 mb-2">
                  <Badge variant="neutral" className="text-xs">
                    {job.strategy.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {job.tags.slice(0, 3).map((tag: string, index: number) => (
                    <Badge key={index} variant="neutral" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {job.tags.length > 3 && (
                    <Badge variant="neutral" className="text-xs">
                      +{job.tags.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleOpen(job.id)} className="flex-1">
                  Open
                </Button>
                {job.bucket === 'to-apply' && (
                  <Button variant="primary" size="sm" className="flex-1">
                    <Send className="h-4 w-4 mr-1" />
                    Apply
                  </Button>
                )}
              </div>
              
              {/* Generate tailored resume button */}
              <Button variant="outline" size="sm" className="w-full">
                Generate tailored resume
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const tabItems = [
    {
      label: 'To Apply',
      value: 'to-apply',
      content: (
        <div className="space-y-4">
          {selectedJobs.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <span className="text-sm">{selectedJobs.length} selected</span>
              <Button variant="outline" size="sm" onClick={() => handleBulkAction('apply')}>
                Move to Applied
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleBulkAction('archive')}>
                Archive
              </Button>
            </div>
          )}
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-5 bg-muted/50 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-muted/50 rounded w-1/2 mb-3"></div>
                    <div className="h-3 bg-muted/50 rounded w-full"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <Card>
              <div className="text-center py-12 bg-destructive/10 rounded-2xl">
                <div className="text-destructive mb-2">Error loading saved jobs</div>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={loadSavedJobs} variant="outline">
                  Try Again
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedJobs
                .filter(job => job.bucket === 'to-apply')
                .map(renderJobCard)
              }
            </div>
          )}
          
          {!loading && !error && savedJobs.filter(job => job.bucket === 'to-apply').length === 0 && (
            <Card>
              <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
                <Bookmark className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No jobs to apply to</h3>
                <p className="text-muted-foreground mb-4">
                  Save interesting jobs to organize your applications.
                </p>
                <Button onClick={() => window.location.href = '/discover'} className="gap-2">
                  Discover Jobs
                </Button>
              </div>
            </Card>
          )}
        </div>
      )
    },
    {
      label: 'Applied',
      value: 'applied',
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {savedJobs
            .filter(job => job.bucket === 'applied')
            .map(renderJobCard)
          }
        </div>
      )
    },
    {
      label: 'Archived',
      value: 'archived',
      content: (
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <Archive className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No archived jobs</h3>
            <p className="text-muted-foreground mb-4">
              Jobs you've archived will appear here.
            </p>
            <Button onClick={() => window.location.href = '/saved?tab=to-apply'} className="gap-2">
              View Active Jobs
            </Button>
          </div>
        </Card>
      )
    }
  ];

  return (
    <div className="container py-6">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Saved Jobs"
          subtitle="Organize jobs into buckets with bulk actions"
        />
        <ResumeSwitcher className="w-64" />
      </div>

      <Tabs items={tabItems} defaultValue="to-apply" />

      <JobDetailSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        job={selectedJob}
        onSave={(id) => console.log('Save:', id)}
        onApply={(id) => console.log('Apply:', id)}
        onReject={(id) => console.log('Reject:', id)}
      />
    </div>
  );
}